import classNames from 'classnames';
import type { FC } from 'react';
import { memo, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { ScrollMenu, VisibilityContext } from 'react-horizontal-scrolling-menu';
import 'react-horizontal-scrolling-menu/dist/styles.css';

import { LoaderBlock } from '../../components/LoaderBlock';
import { frontConfig } from '../../config';
import usePrevious from '../../hooks/usePrevious';
import { useRefLatest } from '../../hooks/useRefLatest';
import { useAppDispatch, useAppSelector, useSelectorMemo } from '../../redux/hooks';
import {
  changeYakliMessageInBbf,
  createYakliSelectorChildMessagesIdsExceptEditor,
  selectYakliTargetMessageId,
} from '../../redux/slices/yakli-slice';
import { useScrollSectionsHelpers } from '../../services/scrollSections';
import { NoSSR } from '../../utils/ssr';
import { LeftArrow, LeftForwardIcon, RightArrow } from './bubbleflow/bubbleFlowArrows';
import type { ClickBubbleFlowHandler } from './bubbleflow/BubbleFlowItem';
import { BubbleFlowItem } from './bubbleflow/BubbleFlowItem';
import { SelectOnScrollEnd } from './bubbleflow/SelectOnScrollEnd';
import { newResponseKeyInBbf } from './thread-service';
import { getStateMsg } from './thread-slice-utils';
import { ThreadNbResponses } from './ThreadNbResponses';
import type { ScrollVisibilityApiType } from './threadTypes';

const isYakliDisplay = true;

interface ThreadMessageBubbleFlowScrollProps {
  parentMessageId: string | undefined;
}

function useValueRef<T>(value: T) {
  const ref = useRef(value);
  ref.current = value;
  return ref;
}

export const ThreadMessageBubbleFlowScroll: FC<ThreadMessageBubbleFlowScrollProps> = memo(
  function ThreadMessageBubbleFlowScroll(props) {
    let { parentMessageId } = props;

    const parentMessageIdRef = useRefLatest(parentMessageId);

    const childMessagesIds = useSelectorMemo(
      () => createYakliSelectorChildMessagesIdsExceptEditor(parentMessageId),
      [parentMessageId],
    );

    const selectedId = useAppSelector(
      s => getStateMsg(s, parentMessageId, isYakliDisplay)?.selectedChildId ?? null,
    );

    const targetedId = useAppSelector(selectYakliTargetMessageId);
    const isTargeted = targetedId === parentMessageId;

    const apiRef = useRef({} as ScrollVisibilityApiType);
    let selectedIdRef = useValueRef(selectedId);

    const currentIndex = useMemo(
      () => childMessagesIds.findIndex(childId => childId === selectedId),
      [childMessagesIds, selectedId],
    );
    // I've seen once a difference between client's isFirstIndex (true) and server's (false), which causes a hydration issue below, with different class names. But I don't know how to reproduce.
    // console.log({ currentIndex, selectedId });

    const { checkCurrentScrollSection } = useScrollSectionsHelpers();

    const isFirstIndex = currentIndex <= 0;
    const isLastIndex = currentIndex >= childMessagesIds.length - 1;

    const ignoreScrollEndWatchRef = useRef(false);
    const ignoreScrollTimerRef = useRef<NodeJS.Timeout>();
    const dispatch = useAppDispatch();

    useEffect(() => {
      // Workaround: when the selection changes, it messes up with the "scrollend" event handler below, that changes the selection after the scroll. To avoid this weird behavior, we set a delay of 1s after any item change when the scrollend event should be ignored. The below code updates this flag and debounces in case the items change very quickly.
      if (selectedId) {
        ignoreScrollEndWatchRef.current = true;
        if (ignoreScrollTimerRef.current) {
          clearTimeout(ignoreScrollTimerRef.current);
          ignoreScrollTimerRef.current = undefined;
        }
        ignoreScrollTimerRef.current = setTimeout(
          () => (ignoreScrollEndWatchRef.current = false),
          1000,
        );
      }
    }, [selectedId]);

    const handleItemClick = useCallback<ClickBubbleFlowHandler>(
      (itemId, options = {}) => {
        if (!apiRef?.current) return;

        let i = childMessagesIds.findIndex(childId => childId === itemId);

        const { offset, fromScroll } = options;
        if (offset != null) {
          i += offset;
        }

        // In case of index out of bound.
        if (i < 0) i = 0;
        if (i > childMessagesIds.length - 1) i = childMessagesIds.length - 1;

        const newSelection = childMessagesIds[i];
        if (!newSelection) {
          throw new Error('Selected response not found');
        }

        // Workaround: see the useEffect above where ignoreScrollEndWatchRef is set for explanations.
        if (fromScroll && ignoreScrollEndWatchRef.current) {
          return;
        }

        dispatch(
          changeYakliMessageInBbf({
            selectedId: newSelection !== selectedIdRef.current ? newSelection : undefined,
            parentMessageId: parentMessageIdRef.current!,
          }),
        );

        // On mobile, update the selected message to have the shadow on the newly opened message.
        // This selection normally changes when scrolling the comments, to select what is visible on the viewport.
        checkCurrentScrollSection();
      },
      [checkCurrentScrollSection, childMessagesIds, dispatch, parentMessageIdRef, selectedIdRef],
    );

    const selectPrevious = useCallback(() => {
      handleItemClick(selectedId, { offset: -1 });
    }, [selectedId, handleItemClick]);

    const selectNext = useCallback(() => {
      handleItemClick(selectedId, { offset: 1 });
    }, [selectedId, handleItemClick]);

    const selectedIdPrev = usePrevious(selectedId);
    const selectionChanged = selectedIdPrev !== selectedId;

    const { isLastItemVisible } = useContext(VisibilityContext);
    const isLastItemVisibleRef = useRefLatest(isLastItemVisible);

    useEffect(() => {
      // selectedId !== newResponseKeyInBbf: editorjs is considered hidden from the bbf, we don't try to scroll in this case.
      if (selectionChanged && selectedId && selectedId !== newResponseKeyInBbf) {
        const { getItemById, scrollToItem } = apiRef.current;

        // The selection changes: set the scroll to the new selection.
        // Small trick: we want the selected item to appear at the second position, so that the previous item remains visible. react-horizontal-scrolling-menu does not support adding a left margin or equivalent, so we tell the library to select the item before the real selection.
        // Below is the index shifting.
        let i = childMessagesIds.findIndex(childId => childId === selectedId);
        const isFirstItem = i === 0;
        const indexToSelectForScroller =
          isFirstItem || (!frontConfig.bbfSelectionAlwaysOnLeft && isLastItemVisibleRef.current)
            ? i
            : i - 1;
        const itemToSelectForScroller = childMessagesIds[indexToSelectForScroller];
        if (!itemToSelectForScroller) {
          // It seems it can happen on a mobile yakli page, when revealing the collapsed messages. The tree is re-rendered at this moment, due to the root message changing, and the old standalone root is hidden.
          console.warn('No ID for selected item, skipping selection:', {
            itemToSelectForScroller,
            childMessagesIds,
            selectedId,
            indexToSelectForScroller,
          });
          return;
        }

        // Workaroud for the first scroll: seen on library stories from creator (https://github.com/asmyshlyaev177/react-horizontal-scrolling-menu/blob/master/stories/5_1_ScrollToItem/ScrollToItem.source.tsx)
        setTimeout(
          () => scrollToItem(getItemById(itemToSelectForScroller), 'smooth', 'start', 'nearest'),
          150,
        );
      }
    }, [childMessagesIds, isLastItemVisibleRef, selectedId, selectionChanged]);

    const loadingChildren = useAppSelector(
      s => getStateMsg(s, parentMessageId, isYakliDisplay)?.loadingChildren,
    );

    if (!childMessagesIds?.length && loadingChildren) {
      return <LoaderBlock />;
    }

    if (!childMessagesIds?.length) {
      return null;
    }

    return (
      <div
        className={classNames(
          'BubbleFlowContainer flex justify-between pr-3',
          isFirstIndex && 'bf-first-selected',
          isTargeted && 'is-targeted',
        )}
      >
        <ScrollMenu
          wrapperClassName="wrapperClassName max-w-[318px]"
          scrollContainerClassName="scrollContainerClassName items-center"
          itemClassName={classNames(
            'flex items-center',
            frontConfig.bbfSelectionAlwaysOnLeft && 'selection-on-left',
          )}
          LeftArrow={
            // Ideally, we shouldn't need NoSSR, but it seems isFirstIndex has a different value on the server and on the client (SSR). See the comment above its definition.
            <NoSSR>
              {isFirstIndex ? (
                <LeftForwardIcon isTargeted={isTargeted} />
              ) : (
                <LeftArrow onClick={selectPrevious} isTargeted={isTargeted} />
              )}
            </NoSSR>
          }
          RightArrow={
            <NoSSR>
              <RightArrow
                disabled={isLastIndex || childMessagesIds.length < 6}
                onClick={selectNext}
                isTargeted={isTargeted}
              />
            </NoSSR>
          }
          apiRef={apiRef}
        >
          {childMessagesIds.map((msg, i) => {
            const itemId = msg as string;
            const isSelectedMessage = msg === selectedId;
            return (
              <BubbleFlowItem
                key={itemId}
                messageId={itemId}
                index={i}
                onClick={handleItemClick}
                isSelectedMessage={isSelectedMessage}
              />
            );
          })}
        </ScrollMenu>
        <ThreadNbResponses parentMessageId={parentMessageId} />
        <SelectOnScrollEnd
          apiRef={apiRef}
          selectedIdRef={selectedIdRef}
          selectItem={handleItemClick}
          ignoreScrollEndWatchRef={ignoreScrollEndWatchRef}
        />
      </div>
    );
  },
);
